import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Mail, Pencil, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { ROLE_LABELS, fmtDate, isStaff } from "@/lib/format";
import InviteUserDialog from "@/components/InviteUserDialog";
import EditUserDialog from "@/components/EditUserDialog";
import { PageHero } from "@/components/PageHero";

function RemoveUser({ u, onConfirm }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="p-1.5 mr-2 text-zinc-500 hover:text-rose-500 transition-colors align-middle" title="Remove user" data-testid={`remove-user-${u.email}`}>
          <Trash2 size={13} />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#111319] border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif-display text-2xl font-normal">Remove {u.name || u.email}?</AlertDialogTitle>
          <AlertDialogDescription>They can no longer sign in to the portal. Their trips and Jet Card requests stay in the records under their name.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="remove-user-cancel-btn">Keep</AlertDialogCancel>
          <AlertDialogAction onClick={() => onConfirm(u)} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="remove-user-confirm-btn">Remove user</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RoleCell({ u, roles, isAdmin, onChange }) {
  if (!isAdmin) return <span className="text-xs text-zinc-300">{ROLE_LABELS[u.role]}</span>;
  return (
    <Select value={u.role} onValueChange={(r) => onChange(u, r)}>
      <SelectTrigger className="h-9 w-[170px] bg-[#0d0f14] border-white/10 text-xs" data-testid={`user-role-select-${u.email}`}><SelectValue /></SelectTrigger>
      <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function Users() {
  const api = useApi();
  const { me } = useMe();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const isAdmin = me.role === "admin";

  const load = useCallback(async () => {
    try {
      const res = await api.get("/users");
      setData(res.data);
    } catch (e) {
      toast.error(errMsg(e));
      setData({ roles: [], users: [] });
    }
  }, [api]);

  useEffect(() => {
    if (isStaff(me)) load();
  }, [load, me]);

  if (!isStaff(me)) return <Navigate to="/" replace />;

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`${u.name} is now ${ROLE_LABELS[role]}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const resend = async (u) => {
    try {
      await api.post(`/users/${u.id}/resend-invite`);
      toast.success(`Invitation re-sent to ${u.email}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name || u.email} removed`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const pending = data?.users.filter((u) => u.role === "pending") || [];
  return (
    <div data-testid="users-page">
      <PageHero testId="users-hero">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div>
          <p className="eyebrow mb-3">{isAdmin ? "Access control" : "Broker network"}</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white">{isAdmin ? "Users & roles" : "Brokers"}</h1>
          <p className="text-zinc-400 text-sm mt-3 max-w-lg">Each broker only sees their own trips and options. The flight department and admins see everyone's.</p>
        </div>
        <button className="frame-btn gold self-start" onClick={() => setOpen(true)} data-testid="invite-user-btn"><UserPlus size={13} /> {isAdmin ? "Add user" : "Add broker"}</button>
      </div>
      </PageHero>

      {isAdmin && pending.length > 0 && (
        <div className="lux-card !border-amber-500/30 p-4 mb-6 text-sm text-amber-200" data-testid="pending-approval-banner">
          {pending.length} sign-up{pending.length > 1 ? "s" : ""} awaiting approval. Assign a role below to grant access.
        </div>
      )}

      <div className="lux-card overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm" data-testid="users-table">
          <thead>
            <tr className="text-left text-[0.58rem] tracking-[0.22em] uppercase text-zinc-500 border-b border-white/[0.07]">
              <th className="px-5 py-3 font-medium">Name</th><th className="px-5 py-3 font-medium">Title</th><th className="px-5 py-3 font-medium">Email</th><th className="px-5 py-3 font-medium">Phone</th>
              <th className="px-5 py-3 font-medium">Role</th><th className="px-5 py-3 font-medium">Status</th><th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={7} className="py-12"><div className="jet-loader mx-auto" /></td></tr>}
            {data?.users.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-zinc-500" data-testid="users-empty">No users yet</td></tr>}
            {data?.users.map((u) => (
              <tr key={u.id} className="border-b border-white/[0.05] hover:bg-white/[0.02]" data-testid={`user-row-${u.email}`}>
                <td className="px-5 py-3 text-white">{u.name || "—"}</td>
                <td className="px-5 py-3 text-zinc-400">{u.title || "—"}</td>
                <td className="px-5 py-3 text-zinc-400">{u.email}</td>
                <td className="px-5 py-3 text-zinc-400" data-testid={`user-phone-${u.id}`}>{u.phone || "—"}{u.office_ext ? <span className="block text-[11px] text-zinc-500">Office ext. {u.office_ext}</span> : ""}</td>
                <td className="px-5 py-3"><RoleCell u={u} roles={data.roles} isAdmin={isAdmin && u.id !== me.id} onChange={changeRole} /></td>
                <td className="px-5 py-3 text-xs">
                  {u.claimed ? <span className="text-emerald-300">Active · {fmtDate(u.joined_at)}</span> : <span className="text-amber-300">Invited · {fmtDate(u.created_at)}</span>}
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {isAdmin && (
                    <button className="p-1.5 mr-2 text-zinc-500 hover:text-[#5B7FD1] transition-colors align-middle" onClick={() => setEditing(u)} title="Edit user" data-testid={`edit-user-${u.email}`}>
                      <Pencil size={13} />
                    </button>
                  )}
                  {isAdmin && u.id !== me.id && <RemoveUser u={u} onConfirm={remove} />}
                  {!u.claimed && (
                    <button className="text-[0.62rem] tracking-[0.2em] uppercase text-zinc-400 hover:text-[#5B7FD1] inline-flex items-center gap-1.5" onClick={() => resend(u)} data-testid={`resend-invite-${u.email}`}>
                      <Mail size={12} /> Resend
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <InviteUserDialog open={open} onOpenChange={setOpen} roles={data?.roles || ["broker"]} onSaved={load} />
      <EditUserDialog user={editing} onOpenChange={(o) => { if (!o) setEditing(null); }} onSaved={load} />
    </div>
  );
}
